import { useState, useEffect, useCallback, useMemo } from "react"
import { Form } from "antd"
import { useSelector } from "react-redux"

/**
 * Custom hook for managing equipment form state and logic
 * Handles form initialization, population from record, and form field state
 *
 * @param {Object} record - Equipment record for editing (optional)
 * @param {boolean} boatLoading - Loading state from parent
 * @param {string|number} boatId - Equipment ID
 * @param {boolean} isDuplicateMode - Whether in duplicate mode
 * @param {Function} getCategoryName - Function that returns category name
 * @param {Object} characteristicsHook - Hook object from useEquipmentCharacteristics
 * @returns {Object} - Form state and handlers
 */
export const useEquipmentForm = (record, boatLoading, boatId, isDuplicateMode, getCategoryName, characteristicsHook) => {
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([]) // experience ids
  const [aiDescriptionData, setAiDescriptionData] = useState("")
  const [showAiModal, setShowAiModal] = useState(false)
  const [profileDescription, setProfileDescription] = useState("")
  const [localMedia, setLocalMedia] = useState([])
  const [filteredMedia, setFilteredMedia] = useState([])
  const [assets, setAssets] = useState([])

  const tripsState = useSelector((state) => state?.trips?.items)

  // Memoized experiences list (stable reference)
  const experiencesMemo = useMemo(
    () =>
      tripsState.map((trip) => ({
        id: String(trip?.id),
        name: trip?.title || "Untitled",
        guests: trip?.max_guests || 0,
        price: trip?.min_price ? Number(trip.min_price) : 0,
        status: trip?.status,
        currency: "$",
        duration: trip?.duration || "-"
      })),
    [tripsState]
  )

  // Derive selected experience data instead of storing separate state (prevents extra render)
  const selectedExperienceData = useMemo(
    () => (selectedExperiences.length ? experiencesMemo.filter((exp) => selectedExperiences.includes(String(exp.id))) : []),
    [selectedExperiences, experiencesMemo]
  )

  // Pre-fill when editing
  const populateFormFromBoat = useCallback(() => {
    if (!record || boatLoading) return
    if (!record.id && boatId) return // still expecting data

    // Normalize trips_id to string array if present
    if (Array.isArray(record?.trips_id)) {
      setSelectedExperiences(record.trips_id.map(String))
      form.setFieldsValue({ experiences: record.trips_id.map(String) })
    } else if (record?.trips_id != null) {
      // Handle single value (number or string)
      setSelectedExperiences([String(record.trips_id)])
    }

    if (record?.asset_details) {
      // In duplicate mode, remove IDs so new assets are created
      setAssets(
        record.asset_details?.map((asset, index) => ({
          key: index + 1,
          id: isDuplicateMode ? null : asset.id || null,
          title: form?.getFieldValue("name") || "",
          category: getCategoryName() || "",
          assetNumber: asset.asset_number || "",
          available: asset.available || false
        }))
      )
    }

    // Characteristics: keep only IDs (stringified)
    if (Array.isArray(record?.characteristics)) {
      const items = record.characteristics
        .filter((c) => {
          if (!c || typeof c !== "object" || c.id == null) return false
          // API can return two structures:
          // 1. { id: master_id, name, association_id: join_id } - from equipment API
          // 2. { id: join_id, boat_characteristic_id: master_id, name } - from other API
          const masterId = c.boat_characteristic_id || c.id
          return masterId != null
        })
        .map((c) => {
          // Handle both API response structures
          const masterId = c.boat_characteristic_id || c.id // master characteristic ID
          const joinRecordId = c.association_id || (c.boat_characteristic_id ? c.id : null) // join record ID

          // Determine characteristicId: null in duplicate mode, otherwise use joinRecordId if available
          let characteristicId = null
          if (!isDuplicateMode && joinRecordId) {
            characteristicId = String(joinRecordId)
          }

          return {
            characteristicId,
            id: String(masterId), // master characteristic id used for selection value
            name: c.name || `Characteristic ${masterId}`
          }
        })

      // Deduplicate items by id to prevent duplicate tags from API response
      const deduplicatedItems = items.filter((item, index, self) =>
        index === self.findIndex((t) => t.id === item.id)
      )

      characteristicsHook.setSelected(deduplicatedItems)
      form.setFieldsValue({ characteristics: deduplicatedItems.map((c) => c.id) }) // form stores master ids
    }

    // Map record fields to form fields (adjust keys based on real API structure)
    form.setFieldsValue({
      name: record?.equipment_title,
      category: record?.category_id ? String(record.category_id) : undefined,
      max_users: record?.max_guests || record?.max_users_count,
      description: record?.description
    })

    if (record?.description) setAiDescriptionData(record.description)

    // Experiences: assume record.experiences is array of {id,...}
    if (Array.isArray(record.experiences)) {
      const expIds = record.experiences.map((e) => String(e.id))
      setSelectedExperiences(expIds)
    }

    // Media: handle either array record.media or single record.image URL
    if (Array.isArray(record?.properties) && record?.properties.length) {
      setLocalMedia(
        record?.properties.map((ele) => {
          return {
            ...ele,
            file_type: ele?.file_type,
            placeholder_url: ele?.placeholder_url,
            preview_url: ele?.preview_url,
            url: ele?.url,
            ids: isDuplicateMode ? null : ele?.id, // In duplicate mode, remove media IDs
            needsConversion: isDuplicateMode // Flag to convert URL to File before submission
          }
        })
      )
    } else if (record.image && typeof record.image === "string") {
      const mediaObj = {
        id: isDuplicateMode ? Date.now() : record.id || Date.now(),
        file_type: "photo",
        url: record.image,
        placeholder_url: record.image,
        preview_url: record.image,
        mediaType: "image", // assuming image is photo
        position: 0,
        uid: "local-rc-upload-1760353752499-9",
        needsConversion: isDuplicateMode // Flag to convert URL to File before submission
      }
      setLocalMedia([mediaObj])
    }
  }, [record, boatLoading, form, boatId, isDuplicateMode])
  // Note: getCategoryName and characteristicsHook intentionally omitted from dependencies
  // - getCategoryName is called to read current value, recreated on every render
  // - characteristicsHook.setSelected is stable, but hook object reference changes every render  
  // Including them would cause infinite loop as they trigger on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Populate when record changes (version increments) or modal becomes visible
  useEffect(() => {
    if (record?.loaded && !boatLoading) {
      populateFormFromBoat()
    }
  }, [record?.version, boatLoading, record?.loaded, populateFormFromBoat])

  const handleRemoveExperience = useCallback(
    (expId) => {
      setSelectedExperiences((prev) => {
        const next = prev.filter((id) => id !== expId)
        // Keep form field in sync so controlled value from Form doesn't push stale tag back
        form.setFieldsValue({ experiences: next })
        return next
      })
    },
    [form]
  )

  return {
    form,
    selectedExperiences,
    setSelectedExperiences,
    selectedExperienceData,
    experiencesMemo,
    handleRemoveExperience,
    assets,
    setAssets,
    localMedia,
    setLocalMedia,
    filteredMedia,
    setFilteredMedia,
    aiDescriptionData,
    setAiDescriptionData,
    showAiModal,
    setShowAiModal,
    profileDescription,
    setProfileDescription
  }
}
